"use client";

import React, { Suspense, useEffect, useLayoutEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import App from "../App";
import i18n, { loadRuntimeTranslation } from "../translation/i18n";
import { loadRedesignContent } from "../redesign/content";
import {
  getClientLanguagePrefix,
  getClientPathname,
  installLocalizedLinkInterceptor
} from "../shared/clientLocale";

const loadJQueryRuntime = async () => {
  if (typeof window === "undefined" || window.jQuery) return;
  const loadedModule = await import("jquery");
  const jQuery = loadedModule.default || loadedModule;
  window.$ = jQuery;
  window.jQuery = jQuery;
};

const LegacySpaApp = ({ onReady }) => {
  const [runtimeReady, setRuntimeReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const language =
      (typeof window !== "undefined" && window.QRCODE_ENV?.DEFAULT_LANGUAGE) ||
      "ja";
    // Path đã bỏ tiền tố ngôn ngữ (/en/products/x → /products/x)
    const pathname = getClientPathname();
    const preloadCurrentRoute = pathname.startsWith("/products/")
      ? import("../redesign/ProductPage")
      : Promise.resolve();

    Promise.all([
      loadJQueryRuntime(),
      loadRuntimeTranslation(),
      loadRedesignContent(language),
      preloadCurrentRoute
    ])
      .then(() => {
        if (!cancelled) setRuntimeReady(true);
      })
      .catch((error) => {
        console.error("Unable to load localized page content.", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    if (runtimeReady && typeof onReady === "function") {
      onReady();
    }
  }, [runtimeReady, onReady]);

  useEffect(() => {
    if (!runtimeReady) return undefined;
    let loaded = false;
    const loadBootstrap = () => {
      if (loaded) return;
      loaded = true;
      import("bootstrap/dist/js/bootstrap.min.js").catch(() => {
        // Ignore bootstrap runtime errors when optional dependencies are missing.
      });
    };
    const interactionEvents = ["pointerdown", "keydown", "touchstart"];
    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, loadBootstrap, {
        once: true,
        passive: true
      });
    });
    const timeoutId = window.setTimeout(loadBootstrap, 5000);
    return () => {
      window.clearTimeout(timeoutId);
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, loadBootstrap);
      });
    };
  }, [runtimeReady]);

  // Lưới an toàn cho <a href="/..."> chưa có tiền tố (markup cũ / nội dung CMS)
  useEffect(() => installLocalizedLinkInterceptor(), []);

  if (!runtimeReady) return null;

  // Tiền tố ngôn ngữ (/en, /fr ...) làm basename: <Link to="/blog"> → /en/blog, history.push cũng vậy.
  const basename = getClientLanguagePrefix();

  return (
    <Router basename={basename || undefined}>
      <I18nextProvider i18n={i18n}>
        <Suspense fallback="">
          <App />
        </Suspense>
      </I18nextProvider>
    </Router>
  );
};

export default LegacySpaApp;
